import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  useUpdateFoodItemDetailsMutation,
  useUploadPhotoMutation,
} from "../slices/FoodItemSilce";
import { toast } from "react-toastify";
import { Button, Col, Form, Row } from "react-bootstrap";
import FieldWarning from "../components/FieldWarning";
import imageCompression from "browser-image-compression";
import ReUsableModal from "../components/ReUsableModal";
import { MdDeleteForever } from "react-icons/md";
import { FaBoxOpen, FaRupeeSign, FaUtensils } from "react-icons/fa";
import {
  cardStyle,
  labelStyle,
  Req,
  SectionCard,
  YesNoRadios,
} from "../components/OutletFormKit";

const EditFoodItemDetails = ({ foodItem, editStatus, setUpdatedFoodItem }) => {
  const { userInfo } = useSelector((state) => state.auth);

  const [itemName, setItemName] = useState(foodItem?.itemName);
  const [localName, setLocalName] = useState(foodItem?.localName);
  const [uniqueItemCode, setUniqueItemCode] = useState(
    foodItem?.uniqueItemCode ?? ""
  );
  const [itemCategory, setItemCategory] = useState(foodItem?.itemCategory);

  const [itemQuantity, setItemQuantity] = useState(foodItem?.itemQuantity);
  const [onlineAvailableThreshold, setOnlineAvailableThreshold] = useState(
    foodItem?.onlineAvailableThreshold ?? 0
  );
  const [itemOriginalPrice, setItemOriginalPrice] = useState(
    foodItem?.itemOriginalPrice ?? 0
  );

  const [discountPercent, setDiscountPercent] = useState(
    foodItem?.discountPercent ?? 0
  );
  const [discountAmount, setDiscountAmount] = useState(
    foodItem?.discountAmount ?? 0
  );
  const [gstTax, setGstTax] = useState(foodItem?.gstTax);
  const [currentlySelling, setCurrentlySelling] = useState(
    foodItem?.currentlySelling
  );
  const [addedBy, setAddedBy] = useState(foodItem?.addedBy);

  const [itemNamePresent, setItemNamePresent] = useState(false);
  const [localNamePresent, setLocalNamePresent] = useState(false);
  const [itemCategoryPresent, setItemCategoryPresent] = useState(false);
  const [itemQuantityPresent, setItemQuantityPresent] = useState(false);
  const [itemOriginalPricePresent, setItemOriginalPricePresent] =
    useState(false);
  const [discountPercentPresent, setDiscountPercentPresent] = useState(false);
  const [discountAmountPresent, setDiscountAmountPresent] = useState(false);
  const [currentlySellingPresent, setCurrentlySellingPresent] = useState(false);
  const [gstTaxPresent, setGstTaxPresent] = useState(false);
  const [addedByPresent, setAddedByPresent] = useState(false);
  const [file, setFile] = useState(null);
  const [fileSizeError, setFileSizeError] = useState(false);
  const [fileInvalidError, setFileInvalidError] = useState(false);
  const [isAvailableInMoreSizes, setIsAvailableInMoreSizes] = useState(
    !!foodItem?.availableSizes?.length
  );
  const [availableSizes, setAvailableSizes] = useState(
    foodItem?.availableSizes?.length || 0
  );
  const [sizesData, setSizesData] = useState(foodItem?.availableSizes || []);
  const [originalSizes, setOriginalSizes] = useState(
    foodItem?.availableSizes || []
  );
  const [description, setDescription] = useState(foodItem?.itemDescription);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [indexToRemove, setIndexToRemove] = useState(null);
  const [isVeg, setIsVeg] = useState(foodItem?.isVeg);
  const [makingCost, setMakingCost] = useState(foodItem?.makingCost ?? 0);

  const [updateFoodItem, { isLoading }] = useUpdateFoodItemDetailsMutation();
  const [uploadPhoto, { isLoading: isPhotoUploading }] =
    useUploadPhotoMutation();

  const categoryOptions =
    userInfo.outletId?.find((oi) => userInfo?.selectedOutletId === oi._id)
      .foodCategories || [];

  useEffect(() => {
    if (itemName?.length > 0) {
      setItemNamePresent(false);
    }

    if (itemCategory?.length > 0) {
      setItemCategoryPresent(false);
    }

    if (itemOriginalPrice > 0) {
      setItemOriginalPricePresent(false);
    }

    if (discountPercent >= 0) {
      setDiscountPercentPresent(false);
    }

    if (discountAmount >= 0) {
      setDiscountAmountPresent(false);
    }

    if (gstTax >= 0) {
      setGstTaxPresent(false);
    }

    if (addedBy?.length > 0) {
      setAddedByPresent(false);
    }

    if (!isAvailableInMoreSizes || availableSizes <= 1) {
      if (itemOriginalPrice > 0) {
        setItemOriginalPricePresent(false);
      }
    }
  }, [
    itemName,
    itemCategory,
    itemOriginalPrice,
    discountPercent,
    discountAmount,
    gstTax,
    addedBy,
  ]);

  const verifyInputFields = () => {
    let fieldValidationSuccess = true;

    if (!itemName || itemName.length < 1 || itemName.length > 100) {
      setItemNamePresent(true);
      fieldValidationSuccess = false;
    }

    if (!itemCategory || itemCategory.length < 1) {
      setItemCategoryPresent(true);
      fieldValidationSuccess = false;
    }

    if (
      discountPercent === undefined ||
      discountPercent === "" ||
      isNaN(discountPercent) ||
      Number(discountPercent) < 0
    ) {
      setDiscountPercentPresent(true);
      fieldValidationSuccess = false;
    }

    if (
      discountAmount === undefined ||
      discountAmount === "" ||
      isNaN(discountAmount) ||
      Number(discountAmount) < 0
    ) {
      setDiscountAmountPresent(true);
      fieldValidationSuccess = false;
    }

    if (
      gstTax === undefined ||
      gstTax === "" ||
      isNaN(gstTax) ||
      Number(gstTax) < 0
    ) {
      setGstTaxPresent(true);
      fieldValidationSuccess = false;
    }

    if (!addedBy || addedBy.length < 1) {
      setAddedByPresent(true);
      fieldValidationSuccess = false;
    }

    if (!isAvailableInMoreSizes) {
      if (
        itemOriginalPrice === undefined ||
        itemOriginalPrice === "" ||
        isNaN(itemOriginalPrice) ||
        Number(itemOriginalPrice) < 1
      ) {
        setItemOriginalPricePresent(true);
        fieldValidationSuccess = false;
      }
    }

    if (!fieldValidationSuccess) {
      toast.error(
        "Some of the fields have invalid inputs. Please provide valid details."
      );
    }

    return fieldValidationSuccess;
  };

  const handleSubmit = async () => {
    if (!verifyInputFields()) return;

    // Build the sizes payload straight from the live sizesData inputs —
    // previously this only sent an edit once the separate "Save Sizes"
    // button had been clicked, and otherwise silently fell back to
    // originalSizes, discarding any unsaved edits (discount %, discount
    // amount, price, etc.) the moment "Save Changes" was pressed directly.
    const formattedSizes = sizesData.map((item) => ({
      sizeName: item.sizeName?.toString().trim(),
      itemPrice: Number(item.itemPrice),
      makingCost: Number(item.makingCost ?? 0),
      discountPercent: Number(item.discountPercent) || 0,
      discountAmount: Number(item.discountAmount) || 0,
      consumptionQty: Number(item.consumptionQty),
      sizeDescription: item.sizeDescription?.toString().trim() || "",
    }));

    for (const sd of formattedSizes) {
      if (!sd.sizeName || !sd.itemPrice || !sd.consumptionQty) {
        toast.error("Size name, Price and Reduce quantity are mandatory");
        return;
      }
    }

    // Merge with the original sizes by name so any fields this form does
    // not expose are preserved rather than dropped.
    const mergedSizes = formattedSizes.map((updatedSize) => {
      const existing = originalSizes.find(
        (os) => os.sizeName === updatedSize.sizeName
      );
      return existing ? { ...existing, ...updatedSize } : updatedSize;
    });

    try {
      const data = {
        _id: foodItem?._id,

        itemName: itemName,
        localName: localName,
        uniqueItemCode: String(uniqueItemCode ?? "").trim(),
        itemCategory: itemCategory,
        itemQuantity: itemQuantity,
        onlineAvailableThreshold: Number(onlineAvailableThreshold) || 0,
        currentlySelling: currentlySelling,
        isVeg: isVeg,
        availableSizes: mergedSizes,
        gstTax: gstTax,
        itemDescription: description,
        makingCost: makingCost,
        addedBy: addedBy,
        token: userInfo.token,
      };
      const resData = await updateFoodItem(data).unwrap();
      if (resData && file) {
        let formData = new FormData();
        formData.append("image", file);
        formData.append("imageKey", `${resData._id}`);

        await uploadPhoto({
          token: userInfo.token,
          payload: formData,
        }).unwrap();
      }

      toast.success("Details updated Successfully");
      editStatus();
      setUpdatedFoodItem(resData);
    } catch (e) {
      toast.error(e?.data?.error || e?.error);
    }
  };

  const handleCancelButton = () => {
    editStatus();
  };

  const compressImage = async (selectedImage) => {
    const file = selectedImage;
    if (file) {
      try {
        const options = {
          maxSizeMB: 1,
          maxWidthOrHeight: 280,
          useWebWorker: true,
          initialQuality: 1,
        };

        console.log("Un-Compressed file:", file);
        const compressedFile = await imageCompression(file, options);

        const secondOptions = {
          maxSizeMB: 1,
          useWebWorker: true,
          initialQuality: 0.9,
        };

        const secondCompressedFile = await imageCompression(
          compressedFile,
          secondOptions
        );
        console.log("Second Compressed file:", secondCompressedFile);
        setFile(secondCompressedFile);
      } catch (error) {
        console.error("Error processing image:", error);
      }
    }
  };
  const handleFileChange = async (e) => {
    const selectedFile = e?.target?.files[0];
    const dotIndex = selectedFile?.name?.lastIndexOf(".");
    const fileExtension = selectedFile?.name?.slice(dotIndex + 1).toLowerCase();
    let errorPresent = false;
    if (
      !(
        fileExtension === "jpeg" ||
        fileExtension === "jpg" ||
        fileExtension === "png" ||
        fileExtension === "tiff" ||
        fileExtension === "svg" ||
        fileExtension === "webp" ||
        fileExtension === "avif" ||
        fileExtension === "jfif"
      )
    ) {
      errorPresent = true;
      setFileInvalidError(true);
      setFile(null);
    } else if (selectedFile?.size / 1000 > 5120) {
      setFileSizeError(true);
      setFile(null);
    } else if (!errorPresent && selectedFile?.size / 1000 <= 5120) {
      setFileSizeError(false);
      setFileInvalidError(false);
      await compressImage(selectedFile);
    }
  };
  useEffect(() => {
    if (foodItem?.availableSizes?.length > 0) {
      const normalized = foodItem.availableSizes.map((s) => ({
        ...s,
        makingCost: typeof s.makingCost !== "undefined" ? s.makingCost : 0,
      }));
      setSizesData(normalized);
      setOriginalSizes(normalized);
      setIsAvailableInMoreSizes(true);
      setAvailableSizes(normalized.length);
    } else if (isAvailableInMoreSizes) {
      setAvailableSizes(2);
    }
  }, [foodItem, isAvailableInMoreSizes]);

  useEffect(() => {
    if (Number(availableSizes) < 1) return;
    const count = Number(availableSizes);
    const initialSizes = Array.from({ length: count }, (_, index) => ({
      sizeName: sizesData[index]?.sizeName ?? "",
      itemPrice: sizesData[index]?.itemPrice ?? "",
      makingCost: sizesData[index]?.makingCost ?? "",
      discountPercent: sizesData[index]?.discountPercent ?? "",
      discountAmount: sizesData[index]?.discountAmount ?? "",
      consumptionQty: sizesData[index]?.consumptionQty ?? "",
      sizeDescription: sizesData[index]?.sizeDescription ?? "",
    }));
    setSizesData(initialSizes);
  }, [availableSizes]);

  const handleSizeDetails = ({ idx, fieldName, value }) => {
    const updated = [...sizesData];

    updated[idx][fieldName] = value;

    setSizesData(updated);
  };

  const handleRemoveSize = (index) => {
    setIndexToRemove(index);
    setShowConfirmModal(true);
  };
  const confirmRemoveSize = () => {
    const updatedSizes = sizesData.filter(
      (_, index) => index !== indexToRemove
    );
    setSizesData(updatedSizes);
    setShowConfirmModal(false);
    setIndexToRemove(null);
  };

  const sizeBoxStyle = {
    border: "1px solid #e3ecf3",
    borderRadius: "10px",
    padding: "0.75rem",
    marginBottom: "0.75rem",
    backgroundColor: "#f9fcfe",
  };

  return (
    <div style={{ width: "100%", marginBottom: "1.5rem" }}>
      <ReUsableModal
        title="Are you sure you want to remove this size?"
        show={showConfirmModal}
        payload={[]}
        onHide={() => setShowConfirmModal(false)}
        onSubmit={confirmRemoveSize}
        submit="Yes"
        cancel="No"
      />

      {/* Header */}
      <div
        style={{ ...cardStyle, padding: "1rem 1.1rem", marginBottom: "0.9rem" }}
      >
        <h5 className="mb-1">
          <strong style={{ color: "#1d5f91" }}>Edit Food Item</strong>
        </h5>
        <small className="text-muted">
          Update details of {foodItem?.itemName || "this item"}
        </small>
      </div>

      <Form>
        {/* Item details */}
        <SectionCard
          icon={<FaUtensils size={15} />}
          title="Item Details"
          subtitle="Name, category and availability"
        >
          <Row style={{ rowGap: "1rem" }}>
            <Col xs={12} md={6}>
              <Form.Label style={labelStyle}>Item Name</Form.Label>
              <Form.Control
                type="text"
                value={itemName}
                onChange={(e) => setItemName(e.target.value)}
              />
              {itemNamePresent && (
                <FieldWarning text="Item name cannot be empty and must be between 1 to 100 characters" />
              )}
            </Col>
            <Col xs={12} md={6}>
              <Form.Label style={labelStyle}>Local Name</Form.Label>
              <Form.Control
                type="text"
                value={localName}
                onChange={(e) => setLocalName(e.target.value)}
              />
            </Col>
            <Col xs={12} md={6}>
              <Form.Label style={labelStyle}>Unique Item Code</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter unique item code"
                value={uniqueItemCode}
                onChange={(e) => setUniqueItemCode(e.target.value)}
              />
            </Col>
            <Col xs={12} md={6}>
              <Form.Label style={labelStyle}>Item Category</Form.Label>
              <Form.Select
                value={itemCategory}
                onChange={(e) => setItemCategory(e.target.value)}
              >
                <option value="">Select Category</option>
                {categoryOptions?.map((category, idx) => (
                  <option key={idx} value={category}>
                    {category}
                  </option>
                ))}
              </Form.Select>
              {itemCategoryPresent && (
                <FieldWarning text="Item category cannot be empty" />
              )}
            </Col>
            <Col xs={12} md={6}>
              <Form.Label style={labelStyle}>Quantity</Form.Label>
              <Form.Control
                type="number"
                value={itemQuantity}
                onChange={(e) => setItemQuantity(e.target.value)}
              />
              {itemQuantityPresent && (
                <FieldWarning text="Quantity must be greater than 0" />
              )}
            </Col>
            <Col xs={12} md={6}>
              <Form.Label style={labelStyle}>
                Online Order Threshold
              </Form.Label>
              <Form.Control
                type="number"
                value={onlineAvailableThreshold}
                onChange={(e) => setOnlineAvailableThreshold(e.target.value)}
              />
            </Col>
            <Col xs={12} sm={6}>
              <Form.Label style={labelStyle}>Veg ?</Form.Label>
              <YesNoRadios
                name="isVegRadio"
                value={isVeg}
                onChange={setIsVeg}
              />
            </Col>
            <Col xs={12} sm={6}>
              <Form.Label style={labelStyle}>Currently Selling ?</Form.Label>
              <YesNoRadios
                name="currentlySellingRadio"
                value={currentlySelling}
                onChange={setCurrentlySelling}
              />
              {currentlySellingPresent && (
                <FieldWarning text="Please select one option" />
              )}
            </Col>
          </Row>
        </SectionCard>

        {/* Pricing */}
        <SectionCard
          icon={<FaRupeeSign size={15} />}
          title="Pricing & Tax"
          subtitle="Cost, tax and size options"
        >
          <Row style={{ rowGap: "1rem" }}>
            <Col xs={6} md={6}>
              <Form.Label style={labelStyle}>Making Cost</Form.Label>
              <Form.Control
                type="text"
                inputMode="numeric"
                value={makingCost}
                onChange={(e) => {
                  const val = e.target.value.replace(/[^0-9]/g, "");
                  setMakingCost(val);
                }}
              />
            </Col>
            <Col xs={6} md={6}>
              <Form.Label style={labelStyle}>GST Tax %</Form.Label>
              <Form.Control
                type="number"
                value={gstTax}
                onChange={(e) => setGstTax(e.target.value)}
              />
              {gstTaxPresent && (
                <FieldWarning text="GST tax cannot be negative" />
              )}
            </Col>

            {(originalSizes.length > 0 || sizesData.length > 0) && (
              <Col xs={12}>
                <div style={{ ...labelStyle, marginBottom: "0.5rem" }}>
                  Available Sizes
                </div>
                {[...sizesData].map((_, index) => (
                  <div key={index} style={sizeBoxStyle}>
                    <div
                      className="d-flex justify-content-between align-items-center"
                      style={{ marginBottom: "0.6rem" }}
                    >
                      <div style={{ ...labelStyle, marginBottom: 0 }}>
                        Size {index + 1}
                      </div>
                      {sizesData?.length > 1 && (
                        <MdDeleteForever
                          style={{
                            cursor: "pointer",
                            color: "#dc3545",
                            width: "1.3rem",
                            height: "1.3rem",
                          }}
                          onClick={() => handleRemoveSize(index)}
                        />
                      )}
                    </div>
                    <Row style={{ rowGap: "0.6rem" }}>
                      <Col xs={6} lg={4}>
                        <Form.Label style={labelStyle}>
                          Size Name
                          <Req />
                        </Form.Label>
                        <Form.Control
                          type="text"
                          placeholder={`Size Name ${index + 1}`}
                          value={sizesData[index]?.sizeName || ""}
                          onChange={(e) =>
                            handleSizeDetails({
                              idx: index,
                              fieldName: "sizeName",
                              value: e.target.value,
                            })
                          }
                        />
                      </Col>
                      <Col xs={6} lg={4}>
                        <Form.Label style={labelStyle}>
                          Price ₹
                          <Req />
                        </Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Price"
                          value={sizesData[index]?.itemPrice || ""}
                          onChange={(e) =>
                            handleSizeDetails({
                              idx: index,
                              fieldName: "itemPrice",
                              value: e.target.value.replace(/[^0-9.]/g, ""),
                            })
                          }
                        />
                      </Col>
                      <Col xs={6} lg={4}>
                        <Form.Label style={labelStyle}>
                          Making Cost ₹
                          <Req />
                        </Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Making Cost"
                          value={sizesData[index]?.makingCost || ""}
                          onChange={(e) =>
                            handleSizeDetails({
                              idx: index,
                              fieldName: "makingCost",
                              value: e.target.value.replace(/[^0-9.]/g, ""),
                            })
                          }
                        />
                      </Col>
                      <Col xs={6} lg={4}>
                        <Form.Label style={labelStyle}>
                          Reduce Qty
                          <Req />
                        </Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Reduce Qty"
                          value={sizesData[index]?.consumptionQty || ""}
                          onChange={(e) => {
                            const input = e.target.value;
                            // Only allow numbers with at most one dot
                            if (/^\d*\.?\d*$/.test(input)) {
                              handleSizeDetails({
                                idx: index,
                                fieldName: "consumptionQty",
                                value: input,
                              });
                            }
                          }}
                        />
                      </Col>
                      <Col xs={6} lg={4}>
                        <Form.Label style={labelStyle}>Discount %</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Discount %"
                          value={sizesData[index]?.discountPercent || ""}
                          onChange={(e) =>
                            handleSizeDetails({
                              idx: index,
                              fieldName: "discountPercent",
                              value: e.target.value.replace(/[^0-9.]/g, ""),
                            })
                          }
                        />
                      </Col>
                      <Col xs={6} lg={4}>
                        <Form.Label style={labelStyle}>Discount ₹</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Discount ₹"
                          value={sizesData[index]?.discountAmount || ""}
                          onChange={(e) =>
                            handleSizeDetails({
                              idx: index,
                              fieldName: "discountAmount",
                              value: e.target.value.replace(/[^0-9.]/g, ""),
                            })
                          }
                        />
                      </Col>
                      <Col xs={12}>
                        <Form.Label style={labelStyle}>
                          Size Description
                        </Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="eg. 250gm"
                          value={sizesData[index]?.sizeDescription || ""}
                          onChange={(e) =>
                            handleSizeDetails({
                              idx: index,
                              fieldName: "sizeDescription",
                              value: e.target.value,
                            })
                          }
                        />
                      </Col>
                    </Row>
                  </div>
                ))}

                <div className="d-flex flex-wrap" style={{ gap: "0.5rem" }}>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() =>
                      setSizesData([
                        ...sizesData,
                        {
                          sizeName: "",
                          itemPrice: "",
                          makingCost: "",
                          discountPercent: "",
                          discountAmount: "",
                          consumptionQty: "",
                          sizeDescription: "",
                        },
                      ])
                    }
                  >
                    + Add One more Size
                  </Button>
                </div>
              </Col>
            )}
          </Row>
        </SectionCard>

        {/* Extras */}
        <SectionCard
          icon={<FaBoxOpen size={15} />}
          title="Extras"
          subtitle="Description, photo and metadata"
        >
          <Row style={{ rowGap: "1rem" }}>
            <Col xs={12} md={6}>
              <Form.Label style={labelStyle}>Item Image</Form.Label>
              <Form.Control
                type="file"
                accept=".jpeg,.jpg,.png,.tiff,.svg,.webp,.avif,.jfif"
                onChange={handleFileChange}
              />
              {file && !fileSizeError && !fileInvalidError && (
                <small style={{ color: "#1e7e34" }}>
                  Image selected and ready to upload
                </small>
              )}
              {fileSizeError && (
                <FieldWarning text="Image size should be less than 5 MB, please select a different image" />
              )}
              {fileInvalidError && (
                <FieldWarning text="Only jpeg, jpg, png, tiff, svg, webp and avif formats are supported" />
              )}
            </Col>
            <Col xs={12} md={6}>
              <Form.Label style={labelStyle}>Added By</Form.Label>
              <Form.Control
                type="text"
                value={addedBy}
                onChange={(e) => setAddedBy(e.target.value)}
              />
              {addedByPresent && (
                <FieldWarning text="Added By field cannot be empty" />
              )}
            </Col>
            <Col xs={12}>
              <Form.Label style={labelStyle}>Item Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter item description"
                style={{ resize: "vertical" }}
              />
            </Col>
          </Row>
        </SectionCard>

        {/* Actions */}
        <div style={{ ...cardStyle, padding: "0.9rem 1rem" }}>
          <div
            className="d-flex gap-2"
            style={{ width: "100%", maxWidth: "460px", margin: "0 auto" }}
          >
            <Button
              className={`${userInfo?.themeColor}ThemeHeaderTop`}
              style={{ flex: 1, padding: "0.6rem", fontWeight: "600" }}
              onClick={handleSubmit}
              disabled={isLoading || isPhotoUploading}
            >
              {isLoading || isPhotoUploading ? "Saving..." : "Save Changes"}
            </Button>
            <Button
              variant="secondary"
              style={{ flex: 1, padding: "0.6rem" }}
              onClick={handleCancelButton}
            >
              Cancel
            </Button>
          </div>
        </div>
      </Form>
    </div>
  );
};

export default EditFoodItemDetails;
